<template>
    <div>
        <nav class="flex" aria-label="Breadcrumb">
            <ol class="flex items-center space-x-4">
                <li>
                    <div>
                        <a href="/admin/pages/desks" class="text-gray-400 hover:text-gray-500">
                            <!-- Heroicon name: solid/home -->
                            <svg class="flex-shrink-0 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                 fill="currentColor" aria-hidden="true">
                                <path
                                    d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                            </svg>
                            <span class="sr-only">Home</span>
                        </a>
                    </div>
                </li>

                <li>
                    <div class="flex items-center">
                        <svg class="flex-shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg"
                             fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z"/>
                        </svg>
                        <a href="/admin/pages/rooms" class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">Räume</a>
                    </div>
                </li>

                <li v-if="!loading">
                    <div class="flex items-center">
                        <svg class="flex-shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg"
                             fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z"/>
                        </svg>
                        <a href="#" class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                           aria-current="page">
                            {{ room.name }}
                        </a>
                    </div>
                </li>
            </ol>
        </nav>

        <headline class="mt-4">
            Raum Ansicht <span class="font-bold">{{ room.name }}</span>

            <template v-slot:more>
                <desk-create class="mr-2" :room="room" @created="load"></desk-create>
                <room-edit :room="room" @reload="load"></room-edit>
            </template>
        </headline>

        <loading v-show="loading"></loading>

        <div v-if="!loading">

            <div class="grid grid-cols-2 gap-2 lg:grid-cols-3">

                <card>
                    <template v-slot:header>
                        Etage
                    </template>

                    <template v-slot:action>
                        <button-secondary @click="showEditFloor = true" v-show="!showEditFloor">
                            <template slot="icon"><i class="fa fa-pencil"></i></template>
                            Raum bearbeiten
                        </button-secondary>
                    </template>

                    <div v-show="!showEditFloor">
                        <a v-if="floor" :href="`/admin/pages/floors/${room.floor_id}`" class="text-xl">
                            {{ floor.name }}
                        </a>

                        <p v-if="!floor" class="text-red-500">
                            Keine Etage
                        </p>
                    </div>

                    <div v-show="showEditFloor">
                        <select-menu
                            class="mb-4"
                            v-model="room.floor_id"
                            :values="floors.map(g => g.id)"
                            :text="floors.map(g => g.name)">
                            Etage
                        </select-menu>

                        <div class="flex justify-end">
                            <button-primary @click="update">
                                <template slot="icon"><i class="fa fa-save"></i></template>
                                Speichern
                            </button-primary>
                        </div>
                    </div>

                </card>


                <card>
                    <template v-slot:header>
                        Zonen
                    </template>

                    <template v-slot:action v-if="false">
                        <button-secondary @click="showEditGroup = true" v-show="!showEditGroup">
                            <i class="fa fa-pencil mr-2"></i>
                            Bearbeiten
                        </button-secondary>
                    </template>

                    <div v-show="!showEditGroup">
                        <zone-item v-for="zone in zones"
                                         :item="zone"
                                         :key="zone.id"></zone-item>

                        <p v-show="zones && zones.length === 0" class="text-red-500">
                            Keine Zone
                        </p>
                    </div>
                </card>

                <card>
                    <template v-slot:header>
                        Personenbegrenzung
                    </template>

                    <p class="text-xl mb-4">
                        <span class="text-gray-500" v-show="!room.has_limit_persons_enabled">
                            {{ room.limit_persons }} (inaktiv)
                        </span>

                        <span v-show="room.has_limit_persons_enabled">
                            {{ room.limit_persons }}
                        </span>
                    </p>

                    <div class="flex" v-show="false">
                        <button-white @click="limit(null)">Limit aufheben</button-white>
                        <button-white @click="limit(Math.ceil(room.desks.length / 2))">50%</button-white>
                        <button-white @click="limit(0)">Raum Schließen</button-white>
                    </div>
                </card>
            </div>

            <div v-if="!loading" class="mt-8">
                <desk-groups @load="load" :groups="groups" :desks="desks" :has-filter="false"></desk-groups>
            </div>
        </div>
    </div>
</template>

<script>
import Headline from "../components/UI/Headline";
import Loading from "../components/UI/Loading";
import DeskCreate from "../components/DeskCreate";
import Card from "../components/UI/Card";
import RoomEdit from "../components/RoomEdit";
import TailwindTable from "../components/UI/TailwindTable";
import DeskEdit from "../components/DeskEdit";
import ButtonSecondary from "../components/UI/ButtonSecondary";
import SelectMenu from "../components/UI/SelectMenu";
import ButtonPrimary from "../components/UI/ButtonPrimary";
import MultiSelect from "../components/UI/MultiSelect.vue";
import ZoneItem from "../components/ZoneItem.vue";
import ButtonWhite from "../components/UI/ButtonWhite.vue";
import ButtonLink from "../components/UI/ButtonLink.vue";
import DeskGroups from "../components/DeskGroups.vue";

export default {
    name: "RoomShow",

    components: {
        DeskGroups,
        ButtonLink,
        ButtonWhite,
        ZoneItem,
        MultiSelect,
        ButtonPrimary,
        SelectMenu,
        ButtonSecondary,
        DeskEdit,
        TailwindTable,
        RoomEdit,
        Card,
        DeskCreate,
        Loading,
        Headline
    },

    props: ['id'],

    data: () => ({
        loading: false,
        room: {},
        groups: [],
        showEditGroup: false,
        showEditFloor: false,
        floors: [],
        desks: [],
        zones: [],
    }),

    mounted() {
        this.load();
    },

    methods: {
        load() {
            this.loading = true;
            this.$http.get(`/admin/rooms/${this.id}`)
                .then((response) => {
                    this.room = response.data.data;
                    this.desks = response.data.data.desks;
                    this.zones = response.data.data.zones;
                    this.groups = response.data.zones;
                    this.floors = response.data.floors;
                    this.loading = false;
                })
                .catch(error => {
                    this.$emit('error', error);
                    this.loading = false;
                });
        },
        update() {
            this.loading = true;
            this.$http.put(`/admin/rooms/${this.room.id}`, {
                name: this.room.name,
                limit_persons: this.room.limit_persons,
                floor_id: this.room.floor_id,
            }).then(() => {
                this.load();
                this.showEditGroup = false;
                this.showEditFloor = false;
            }).catch(error => {
                this.loading = false;
                this.$root.$emit('error', error);
            })
        },
        limit(number) {
            this.room.limit_persons = number;
            this.update();
        }
    }
}
</script>

<style scoped>

</style>
